import React, { useState, useEffect, useRef } from 'react';
import { Award, PenSquare, Plus, Trash2, X, Check } from 'lucide-react';
import { CandidateProfile } from '../../services/candidateServiceApi';
import { useDirtyState } from '../../hooks/useDirtyState';

interface Certification {
    name: string;
    issuer: string;
    issueDate: string;
    expirationDate: string;
    credentialId: string;
    url: string;
}

interface CertificationsSectionProps {
    profile: CandidateProfile;
    onSave?: (certifications: Certification[]) => Promise<void>;
}

// Pre-populated FINRA and standard financial licenses/certifications
const COMMON_CERTIFICATIONS = [
    'Series 6 - Investment Company and Variable Contracts Products Representative',
    'Series 7 - General Securities Representative',
    'Series 24 - General Securities Principal',
    'Series 63 - Uniform Securities Agent State Law',
    'Series 65 - Uniform Investment Adviser Law',
    'Series 66 - Uniform Combined State Law',
    'SIE - Securities Industry Essentials',
    'CFA - Chartered Financial Analyst',
    'CFP - Certified Financial Planner',
    'CPA - Certified Public Accountant',
    'CAIA - Chartered Alternative Investment Analyst',
    'FRM - Financial Risk Manager',
    'CMA - Certified Management Accountant',
    'EA - Enrolled Agent',
    'Life & Health Insurance License',
    'Property & Casualty Insurance License',
];

const CertificationsSection: React.FC<CertificationsSectionProps> = ({ profile, onSave }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editedCerts, setEditedCerts] = useState<Certification[]>([]);
    const [showSuggestions, setShowSuggestions] = useState<number | null>(null);
    const [suggestionFilter, setSuggestionFilter] = useState('');
    const suggestionsRef = useRef<HTMLDivElement>(null);

    // Get certifications from profile
    const certifications = profile?.parsedResume?.certifications ||
                          profile?.parsedResume?.rawJson?.certifications ||
                          profile?.resume_data?.parsed_data?.certifications ||
                          [];

    // Track dirty state
    const { isDirty, setOriginal, reset: resetDirty } = useDirtyState(editedCerts);

    useEffect(() => {
        if (isEditing) {
            const initial = certifications.map((cert: any) => ({
                name: cert.name || cert.certification_name || '',
                issuer: cert.issuer || cert.issuing_organization || '',
                issueDate: cert.issueDate || cert.issue_date || '',
                expirationDate: cert.expirationDate || cert.expiration_date || '',
                credentialId: cert.credentialId || cert.credential_id || '',
                url: cert.url || '',
            }));
            setEditedCerts(initial);
            setOriginal(initial);
        } else {
            resetDirty();
        }
    }, [isEditing]);

    // Close suggestions on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
                setShowSuggestions(null);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const handleEdit = () => setIsEditing(true);

    const handleCancel = () => {
        setIsEditing(false);
        setEditedCerts([]);
        setShowSuggestions(null);
    };

    const handleSave = async () => {
        if (onSave) {
            setIsSaving(true);
            try {
                await onSave(editedCerts);
                setIsEditing(false);
            } catch (error) {
                console.error('Failed to save certifications:', error);
            } finally {
                setIsSaving(false);
            }
        } else {
            setIsEditing(false);
        }
    };

    const handleAddCert = () => {
        setEditedCerts([
            ...editedCerts,
            { name: '', issuer: '', issueDate: '', expirationDate: '', credentialId: '', url: '' }
        ]);
    };

    const handleRemoveCert = (index: number) => {
        setEditedCerts(editedCerts.filter((_, i) => i !== index));
    };

    const handleCertChange = (index: number, field: keyof Certification, value: string) => {
        const updated = [...editedCerts];
        updated[index] = { ...updated[index], [field]: value };
        setEditedCerts(updated);

        if (field === 'name') {
            setSuggestionFilter(value);
            setShowSuggestions(value.length > 0 ? index : null);
        }
    };

    const handleSelectSuggestion = (index: number, suggestion: string) => {
        const updated = [...editedCerts];
        updated[index] = { ...updated[index], name: suggestion };
        setEditedCerts(updated);
        setShowSuggestions(null);
        setSuggestionFilter('');
    };

    const filteredSuggestions = COMMON_CERTIFICATIONS.filter(cert =>
        cert.toLowerCase().includes(suggestionFilter.toLowerCase())
    );

    if (certifications.length === 0 && !isEditing) {
        return (
            <div className="bg-white rounded-2xl p-6 w-full border border-[#EDEDED]">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Award size={20} className="text-[#0B111E]" />
                        <h3
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '18px',
                                lineHeight: '25px',
                                color: '#0B111E',
                            }}
                        >
                            Licenses & Certifications
                        </h3>
                    </div>
                    <button
                        onClick={handleEdit}
                        className="text-[#015FA6] hover:bg-blue-50 p-2 rounded-full transition-colors"
                    >
                        <Plus size={20} />
                    </button>
                </div>
                <p className="text-sm text-gray-500">No certifications added yet.</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl border border-[#EDEDED] p-6 w-full font-manrope">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-2">
                    <Award size={20} className="text-[#0B111E]" />
                    <h3 className="text-lg font-semibold text-[#0B111E]">Licenses & Certifications</h3>
                </div>
                {!isEditing ? (
                    <button
                        onClick={handleEdit}
                        className="text-[#015FA6] hover:bg-blue-50 p-2 rounded-full transition-colors"
                    >
                        <PenSquare size={20} />
                    </button>
                ) : (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleCancel}
                            className="text-gray-500 hover:bg-gray-100 p-2 rounded-full transition-colors"
                            disabled={isSaving}
                        >
                            <X size={20} />
                        </button>
                        <button
                            onClick={handleSave}
                            className={`p-2 rounded-full transition-colors ${
                                isDirty ? 'text-green-600 hover:bg-green-50' : 'text-gray-300 cursor-not-allowed'
                            }`}
                            disabled={isSaving || !isDirty}
                        >
                            <Check size={20} />
                        </button>
                    </div>
                )}
            </div>

            {isEditing ? (
                <div className="space-y-4">
                    {editedCerts.map((cert, index) => (
                        <div key={index} className="p-4 border border-[#EDEDED] rounded-lg bg-gray-50">
                            <div className="flex justify-between items-start mb-3">
                                <span className="text-sm font-medium text-gray-600">Certification {index + 1}</span>
                                <button
                                    onClick={() => handleRemoveCert(index)}
                                    className="text-red-500 hover:bg-red-50 p-1 rounded transition-colors"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="relative" ref={showSuggestions === index ? suggestionsRef : undefined}>
                                    <label className="block text-xs text-gray-500 mb-1">Certification Name</label>
                                    <input
                                        type="text"
                                        value={cert.name}
                                        onChange={(e) => handleCertChange(index, 'name', e.target.value)}
                                        onFocus={() => {
                                            if (cert.name.length > 0) {
                                                setSuggestionFilter(cert.name);
                                                setShowSuggestions(index);
                                            }
                                        }}
                                        placeholder="e.g. Series 7, CFA, CPA"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                    />
                                    {showSuggestions === index && filteredSuggestions.length > 0 && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                            {filteredSuggestions.map((suggestion, sIdx) => (
                                                <button
                                                    key={sIdx}
                                                    onClick={() => handleSelectSuggestion(index, suggestion)}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 hover:text-[#015FA6] transition-colors"
                                                >
                                                    {suggestion}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Issuing Organization</label>
                                    <input
                                        type="text"
                                        value={cert.issuer}
                                        onChange={(e) => handleCertChange(index, 'issuer', e.target.value)}
                                        placeholder="e.g. FINRA, CFA Institute"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Issue Date</label>
                                    <input
                                        type="month"
                                        value={cert.issueDate}
                                        onChange={(e) => handleCertChange(index, 'issueDate', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Expiration Date</label>
                                    <input
                                        type="month"
                                        value={cert.expirationDate}
                                        onChange={(e) => handleCertChange(index, 'expirationDate', e.target.value)}
                                        placeholder="No expiration"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Credential ID</label>
                                    <input
                                        type="text"
                                        value={cert.credentialId}
                                        onChange={(e) => handleCertChange(index, 'credentialId', e.target.value)}
                                        placeholder="Optional"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Credential URL</label>
                                    <input
                                        type="url"
                                        value={cert.url}
                                        onChange={(e) => handleCertChange(index, 'url', e.target.value)}
                                        placeholder="Optional"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                    <button
                        onClick={handleAddCert}
                        className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-[#015FA6] hover:text-[#015FA6] transition-colors flex items-center justify-center gap-2"
                    >
                        <Plus size={18} />
                        Add Certification
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {certifications.map((cert: any, index: number) => {
                        const name = cert.name || cert.certification_name || 'Certification';
                        const issuer = cert.issuer || cert.issuing_organization || '';
                        const issueDate = cert.issueDate || cert.issue_date || '';
                        const expDate = cert.expirationDate || cert.expiration_date || '';

                        const dateDisplay = issueDate
                            ? expDate ? `${issueDate} - ${expDate}` : `Issued ${issueDate}`
                            : '';

                        const secondLine = [issuer, dateDisplay].filter(Boolean).join(' | ');

                        return (
                            <div key={index} className="p-3 border border-[#F4F4F4] rounded-lg hover:border-[#015FA6]/30 transition-colors">
                                <h4 className="text-sm font-medium text-[#515151] mb-1">{name}</h4>
                                {secondLine && <p className="text-sm text-[#757575]">{secondLine}</p>}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CertificationsSection;
